import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../api/client';

type Esteira = 'fria' | 'fala_sem_area' | 'area_sem_sara' | 'engajada' | 'retorno';

interface LinhaEmail {
  posicao: number;
  emailKey: string;
  assunto: string;
  cta: 'area' | 'whatsapp' | 'ambos';
  agendados: number;
  enviados: number;
  entregues: number;
  abertos: number;
  cliques: number;
  falhas: number;
  proximoEnvio: string | null;
  txAbertura: number | null;
  txClique: number | null;
}
interface BlocoEsteira {
  esteira: Esteira;
  label: string;
  pausa: boolean;
  assinantes: Record<string, number>;
  totalAssinantes: number;
  enviados: number;
  abertos: number;
  cliques: number;
  descadastros: number;
  txAbertura: number | null;
  txClique: number | null;
  emails: LinhaEmail[];
}
interface Overview {
  pausaGeral: boolean;
  backfillFeitoEm: string | null;
  enviados: number;
  abertos: number;
  cliques: number;
  txAbertura: number | null;
  txClique: number | null;
  envios: Record<string, number>;
}
interface ContagemEsteira {
  esteira: Esteira;
  candidatos: number;
  novos: number;
  jaInscritos: number;
  suprimidos: number;
}
interface Contagem {
  elegiveis: number;
  porEsteira: ContagemEsteira[];
  totalNovos: number;
}
interface Preview {
  emailKey: string;
  assunto: string;
  cta: string;
  html: string;
}

// Desde 26/09/2026 os dois destinos moram no app da Semente: `area` é "Seus
// materiais" e `whatsapp` é "Conversar com a Sara" (os valores ficaram por
// compatibilidade com o backend; só o rótulo mudou).
const CTA_LABEL: Record<string, string> = {
  area: 'App: Seus materiais',
  whatsapp: 'App: chat da Sara',
  ambos: 'App: materiais + chat',
};

function GlassCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`nut-glass rounded-2xl ${className}`}>{children}</div>;
}

/**
 * Caixa de erro das consultas. Existe porque a primeira versão desta tela
 * renderizava só `{data && ...}`: quando a API falhava, a página ficava em
 * branco sem dizer nada. Foi exatamente o que aconteceu em 29/07/2026 — o
 * painel estava apontado pra instância EN (seletor PT/EN no topo), que não tem
 * este módulo, e a tela pareceu quebrada em vez de explicar o motivo.
 */
function ErroBox({ o_que, erro }: { o_que: string; erro: unknown }) {
  const status = (erro as { status?: number } | null)?.status;
  const msg = (erro as { message?: string } | null)?.message ?? 'erro desconhecido';
  return (
    <div
      role="alert"
      className="rounded-xl bg-rose-400/10 px-4 py-3 text-sm text-rose-200 ring-1 ring-rose-300/25"
    >
      Não consegui carregar {o_que}
      {status ? ` (HTTP ${status})` : ''}: {msg}.
      {status === 404 && (
        <>
          {' '}
          <strong>404 quase sempre é o seletor PT/EN/ES no topo da página</strong>: as instâncias em
          inglês e em espanhol são outros backends e não têm a sequência de ativação. Clica em PT.
        </>
      )}
    </div>
  );
}

function Carregando({ o_que }: { o_que: string }) {
  return (
    <div className="flex items-center gap-2 py-6 text-white/50">
      <span className="animate-pulse">✦</span> carregando {o_que}…
    </div>
  );
}

function dataBR(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function taxa(v: number | null): string {
  return v === null ? '—' : `${v.toFixed(1)}%`;
}

/** Destaca a taxa de clique contra o piso da nutrição (~0,4%). */
function corDoClique(v: number | null): string {
  if (v === null) return 'text-white/40';
  if (v >= 1) return 'text-emerald-300';
  if (v >= 0.4) return 'text-amber-300';
  return 'text-rose-300';
}

function Toggle({
  ligado,
  onChange,
  disabled,
  rotulos = ['Rodando', 'Pausada'],
}: {
  ligado: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  rotulos?: [string, string] | string[];
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!ligado)}
      disabled={disabled}
      aria-pressed={ligado}
      className={`rounded-full px-4 py-1.5 text-xs font-medium transition disabled:opacity-50 ${
        ligado
          ? 'bg-emerald-400/15 text-emerald-200 ring-1 ring-emerald-300/30'
          : 'bg-rose-400/15 text-rose-200 ring-1 ring-rose-300/30'
      }`}
    >
      {ligado ? `▶ ${rotulos[0]}` : `⏸ ${rotulos[1]}`}
    </button>
  );
}

export function AtivacaoPage() {
  const qc = useQueryClient();
  const [aberta, setAberta] = useState<Esteira | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [dia0, setDia0] = useState('');

  const overview = useQuery({
    queryKey: ['ativacao', 'overview'],
    queryFn: () => request<Overview>('/admin/ativacao/overview'),
  });
  const esteiras = useQuery({
    queryKey: ['ativacao', 'esteiras'],
    queryFn: () => request<BlocoEsteira[]>('/admin/ativacao/esteiras'),
  });
  const contagem = useQuery({
    queryKey: ['ativacao', 'contar'],
    queryFn: () => request<Contagem>('/admin/ativacao/backfill/contar'),
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['ativacao'] });
  };

  const pausaGeral = useMutation({
    mutationFn: (pausa: boolean) =>
      request('/admin/ativacao/config', { method: 'PUT', body: { pausa } }),
    onSuccess: invalidar,
  });
  const pausaEsteira = useMutation({
    mutationFn: ({ esteira, pausa }: { esteira: Esteira; pausa: boolean }) =>
      request(`/admin/ativacao/esteira/${esteira}/config`, { method: 'PUT', body: { pausa } }),
    onSuccess: invalidar,
  });
  const backfill = useMutation({
    mutationFn: (esteiras?: Esteira[]) =>
      request('/admin/ativacao/backfill', {
        method: 'POST',
        body: { dia0: dia0 ? new Date(dia0).toISOString() : undefined, esteiras },
      }),
    onSuccess: invalidar,
  });
  const verPreview = useMutation({
    mutationFn: (emailKey: string) =>
      request<Preview>(`/admin/ativacao/preview/${encodeURIComponent(emailKey)}`),
    onSuccess: (d) => setPreview(d),
  });

  const ov = overview.data;
  const blocos = esteiras.data ?? [];

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-white/90">Ativação</h1>
          <p className="mt-1 max-w-2xl text-sm text-white/45">
            Cinco esteiras. Quatro delas são de entrada: cada pessoa cai em uma só, escolhida por já
            falar com a Sara × já usar a área de membros. A quinta é a de{' '}
            <strong className="text-white/70">retorno</strong>, que recolhe quem já terminou a régua
            e ficou mais de 30 dias sem receber nada. Roda em paralelo com a nutrição.
          </p>
        </div>
        {ov && (
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.14em] text-white/40">Freio geral</span>
            <Toggle
              ligado={!ov.pausaGeral}
              disabled={pausaGeral.isPending}
              onChange={(v) => pausaGeral.mutate(!v)}
              rotulos={['Tudo rodando', 'Tudo pausado']}
            />
          </div>
        )}
      </header>

      {overview.isPending && <Carregando o_que="os números" />}
      {overview.isError && <ErroBox o_que="os números" erro={overview.error} />}

      {ov?.pausaGeral && (
        <div className="rounded-xl bg-amber-400/10 px-4 py-3 text-sm text-amber-200 ring-1 ring-amber-300/25">
          O freio geral está puxado — <strong>nenhuma esteira dispara</strong>, mesmo as que estão
          marcadas como rodando. Nada é perdido: os envios ficam parados em pendente e saem quando
          você soltar.
        </div>
      )}

      {ov && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {[
            ['Enviados', ov.enviados.toLocaleString('pt-BR')],
            ['Pendentes', (ov.envios.pendente ?? 0).toLocaleString('pt-BR')],
            ['Abertos', ov.abertos.toLocaleString('pt-BR')],
            ['Taxa de abertura', taxa(ov.txAbertura)],
            ['Taxa de clique', taxa(ov.txClique)],
          ].map(([t, v]) => (
            <GlassCard key={t} className="p-4">
              <div className="text-[11px] uppercase tracking-[0.14em] text-white/45">{t}</div>
              <div className="mt-2 font-serif text-2xl nut-gold-text">{v}</div>
            </GlassCard>
          ))}
        </div>
      )}

      {/* ------------------------------------------------ inscrição em massa */}
      <GlassCard className="p-5">
        <h2 className="font-serif text-xl text-white/85">Inscrever a base</h2>
        <p className="mt-1 text-sm text-white/45">
          Classifica todo mundo que comprou o front (ativa/pendente, sem opt-out) e inscreve na
          esteira certa. É idempotente: quem já está inscrito não entra de novo.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="text-xs text-white/50">
            <span className="mb-1 block uppercase tracking-[0.14em]">Primeiro e-mail em</span>
            <input
              type="datetime-local"
              value={dia0}
              onChange={(e) => setDia0(e.target.value)}
              className="rounded-lg bg-white/5 px-3 py-2 text-sm text-white/85 ring-1 ring-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-300/60"
            />
          </label>
          <button
            type="button"
            onClick={() => {
              // Inscrever em massa dispara e-mail pra MUITA gente e não tem
              // desfazer (achado D10-06). Confirmação com o número na frente.
              if (
                window.confirm(
                  `Inscrever ${contagem.data?.totalNovos ?? 0} pessoa(s) em TODAS as esteiras? ` +
                    'Isso começa a mandar e-mail pra elas e não tem como desfazer.',
                )
              ) {
                backfill.mutate(undefined);
              }
            }}
            disabled={backfill.isPending}
            className="rounded-lg bg-amber-300/15 px-4 py-2 text-sm font-medium text-amber-200 ring-1 ring-amber-300/30 transition hover:bg-amber-300/25 disabled:opacity-50"
          >
            {backfill.isPending ? 'Inscrevendo…' : 'Inscrever todas as esteiras'}
          </button>
          {contagem.isError && (
            <span className="text-sm text-rose-200/80">
              não consegui contar quem entraria — veja o erro acima
            </span>
          )}
          {contagem.data && (
            <span className="text-sm text-white/50">
              {contagem.data.elegiveis.toLocaleString('pt-BR')} elegíveis ·{' '}
              <strong className="text-white/80">
                {contagem.data.totalNovos.toLocaleString('pt-BR')} entrariam agora
              </strong>
            </span>
          )}
        </div>
      </GlassCard>

      {/* ------------------------------------------------------- as esteiras */}
      {esteiras.isPending && <Carregando o_que="as esteiras" />}
      {esteiras.isError && <ErroBox o_que="as esteiras" erro={esteiras.error} />}
      {esteiras.isSuccess && blocos.length === 0 && (
        <GlassCard className="p-6 text-sm text-white/50">
          Nenhuma esteira ainda. Use <strong>Inscrever a base</strong> acima pra classificar as
          pessoas e agendar os e-mails.
        </GlassCard>
      )}

      {blocos.map((b) => {
        const c = contagem.data?.porEsteira.find((x) => x.esteira === b.esteira);
        const expandida = aberta === b.esteira;
        return (
          <GlassCard key={b.esteira} className="overflow-hidden">
            <div className="flex flex-wrap items-center gap-4 p-5">
              <button
                type="button"
                onClick={() => setAberta(expandida ? null : b.esteira)}
                aria-expanded={expandida}
                className="flex-1 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-white/40">{expandida ? '▾' : '▸'}</span>
                  <span className="font-serif text-lg text-white/90">{b.label}</span>
                </div>
                <div className="mt-1 pl-5 text-xs text-white/45">
                  {b.totalAssinantes.toLocaleString('pt-BR')} inscritas · {b.emails.length} e-mails
                  · {b.descadastros} descadastros
                  {c && c.novos > 0 && (
                    <span className="text-amber-200/80">
                      {' '}
                      · {c.novos.toLocaleString('pt-BR')} ainda entrariam
                    </span>
                  )}
                </div>
              </button>
              <div className="flex items-center gap-5 text-right">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.14em] text-white/40">
                    Abertura
                  </div>
                  <div className="text-lg text-white/80">{taxa(b.txAbertura)}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.14em] text-white/40">
                    Clique
                  </div>
                  <div className={`text-lg ${corDoClique(b.txClique)}`}>{taxa(b.txClique)}</div>
                </div>
                <Toggle
                  ligado={!b.pausa}
                  disabled={pausaEsteira.isPending}
                  onChange={(v) => pausaEsteira.mutate({ esteira: b.esteira, pausa: !v })}
                />
              </div>
            </div>

            {expandida && (
              <div className="border-t border-white/10 bg-black/20">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[820px] text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-white/40">
                        <th className="px-5 py-3">#</th>
                        <th className="px-3 py-3">Assunto</th>
                        <th className="px-3 py-3">CTA</th>
                        <th className="px-3 py-3">Próximo</th>
                        <th className="px-3 py-3 text-right">Enviados</th>
                        <th className="px-3 py-3 text-right">Abertura</th>
                        <th className="px-3 py-3 text-right">Clique</th>
                        <th className="px-5 py-3" />
                      </tr>
                    </thead>
                    <tbody className="text-white/75">
                      {b.emails.map((e) => (
                        <tr key={e.emailKey} className="border-t border-white/5">
                          <td className="px-5 py-3 text-white/40">{e.posicao}</td>
                          <td className="px-3 py-3">{e.assunto}</td>
                          <td className="px-3 py-3 text-xs text-white/50">
                            {CTA_LABEL[e.cta] ?? e.cta}
                          </td>
                          <td className="px-3 py-3 text-xs text-white/50">
                            {dataBR(e.proximoEnvio)}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {e.enviados.toLocaleString('pt-BR')}
                            {e.falhas > 0 && (
                              <span className="ml-1 text-rose-300/80">({e.falhas} falhas)</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right">{taxa(e.txAbertura)}</td>
                          <td className={`px-3 py-3 text-right ${corDoClique(e.txClique)}`}>
                            {taxa(e.txClique)}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => verPreview.mutate(e.emailKey)}
                              className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-white/70 ring-1 ring-white/10 transition hover:bg-white/10"
                            >
                              Ver e-mail
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-3 px-5 py-4">
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Inscrever nesta esteira ("${b.esteira}")? ` +
                            'Isso começa a mandar e-mail pra elas e não tem como desfazer.',
                        )
                      ) {
                        backfill.mutate([b.esteira]);
                      }
                    }}
                    disabled={backfill.isPending}
                    className="rounded-lg bg-white/5 px-4 py-2 text-xs text-white/70 ring-1 ring-white/10 transition hover:bg-white/10 disabled:opacity-50"
                  >
                    Inscrever só esta esteira
                  </button>
                </div>
              </div>
            )}
          </GlassCard>
        );
      })}

      {/* ------------------------------------------------------------ preview */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-6"
          role="dialog"
          aria-modal="true"
          aria-label={`Preview: ${preview.assunto}`}
        >
          <div className="w-full max-w-3xl">
            <div className="mb-3 flex items-center justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-white/40">
                  {preview.emailKey} · {CTA_LABEL[preview.cta] ?? preview.cta}
                </div>
                <div className="font-serif text-lg text-white/90">{preview.assunto}</div>
              </div>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="rounded-lg bg-white/10 px-4 py-2 text-sm text-white/80 ring-1 ring-white/15 transition hover:bg-white/20"
              >
                Fechar
              </button>
            </div>
            <iframe
              title={`Preview do e-mail ${preview.emailKey}`}
              srcDoc={preview.html}
              className="h-[75vh] w-full rounded-xl border-0 bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}
